// @ts-check

import { ALL_CANDIDATES, CELL_COUNT, digitMask } from "./constants.js";
import { PEERS_BY_CELL, UNITS } from "./topology.js";

export function parseBoard(value) {
  if (
    typeof value !== "string" ||
    value.length !== CELL_COUNT ||
    /[^0-9.]/.test(value)
  ) {
    throw new Error("A Sudoku board must contain exactly 81 digits or dots.");
  }
  return Uint8Array.from(value, (character) =>
    character === "." ? 0 : Number(character),
  );
}

export function serializeBoard(board) {
  if (!board || board.length !== CELL_COUNT)
    throw new Error("Board must contain 81 cells.");
  return Array.from(board).join("");
}

export function validateGivens(input) {
  let board;
  try {
    board =
      typeof input === "string" ? parseBoard(input) : Uint8Array.from(input);
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
  if (board.length !== CELL_COUNT || board.some((digit) => digit > 9))
    return { valid: false, error: "Board contains invalid cells." };
  for (const unit of UNITS) {
    let seen = 0;
    for (const cell of unit) {
      const digit = board[cell];
      if (!digit) continue;
      const mask = digitMask(digit);
      if (seen & mask)
        return {
          valid: false,
          error: "Duplicate given in a row, column, or box.",
          cell,
        };
      seen |= mask;
    }
  }
  return { valid: true, board };
}

export function candidateMask(board, cell) {
  if (board[cell]) return 0;
  let unavailable = 0;
  for (const peer of PEERS_BY_CELL[cell]) unavailable |= digitMask(board[peer]);
  return ALL_CANDIDATES & ~unavailable;
}

export function candidateMasks(board) {
  return Uint16Array.from(board, (digit, cell) =>
    digit ? 0 : candidateMask(board, cell),
  );
}

export function conflictsFor(board, cell) {
  const digit = board[cell];
  if (!digit) return [];
  return PEERS_BY_CELL[cell].filter((peer) => board[peer] === digit);
}

export function isValidCompletedBoard(board) {
  if (
    !board ||
    board.length !== CELL_COUNT ||
    board.some((digit) => digit < 1 || digit > 9)
  )
    return false;
  return UNITS.every(
    (unit) => new Set(unit.map((cell) => board[cell])).size === 9,
  );
}

export function completionRatio(board) {
  return Array.from(board).filter(Boolean).length / CELL_COUNT;
}
